import { marketplaceStateLabel } from "@nova-ai/shared"
import type { OrderListItem } from "../../services/use-orders"
import { marketplaceChipKind } from "../../lib/marketplace"
import { omsChipKind, omsStateLabel } from "../../lib/oms"
import { convertToEur, type ExchangeRates } from "../../lib/money"

export type QueueStageTone = "good" | "warn" | "bad" | "info" | "muted"
export type QueueStage = { label: string; value: string; tone: QueueStageTone }

export function orderStages(order: OrderListItem): QueueStage[] {
  return [
    {
      label: "Marketplace",
      value: marketplaceStateLabel(order.state),
      tone: marketplaceTone(order.state),
    },
    {
      label: "Baselinker",
      value: order.baselinkerState ?? "Not linked",
      tone: order.baselinkerOrderId ? "info" : "muted",
    },
    {
      label: "Magento",
      value: magentoQueueLabel(order),
      tone: magentoQueueTone(order),
    },
    {
      label: "OMS",
      value: order.omsState ? omsStateLabel(order.omsState) : "Not linked",
      tone: order.omsState ? omsChipKind(order.omsState) : "muted",
    },
  ]
}
function marketplaceTone(state: string): QueueStageTone {
  return marketplaceChipKind(state) ?? "muted"
}

function magentoQueueLabel(order: OrderListItem): string {
  if (order.magentoOrderId) {
    return order.magentoOrderId
  }
  if (order.magentoState === "ambiguous") {
    return `${order.magentoMatchCount} matches`
  }
  if (order.magentoState === "not_found") {
    return "Not found"
  }
  if (order.magentoState === "error") {
    return "Sync error"
  }
  return "Not linked"
}

function magentoQueueTone(order: OrderListItem): QueueStageTone {
  if (order.magentoOrderId) {
    return "good"
  }
  if (order.magentoState === "error") {
    return "bad"
  }
  if (order.magentoState === "ambiguous" || order.magentoState === "not_found") {
    return "warn"
  }
  return "muted"
}

export function headlineStage(stages: readonly QueueStage[]): QueueStage | undefined {
  return (
    stages.find((stage) => stage.tone === "warn" || stage.tone === "bad") ??
    stages.findLast((stage) => stage.tone !== "muted") ??
    stages[0]
  )
}

export type DayStats = { count: number; totalsByCurrency: Map<string, number> }

export function localDayKey(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

/** Day subtotals over the loaded rows: per local day, an order count and totals
 *  by currency. Grows as more pages load. */
export function buildDayStats(
  rows: readonly Pick<OrderListItem, "createdAt" | "currency" | "totalPrice">[]
): Map<string, DayStats> {
  const dayStats = new Map<string, DayStats>()
  for (const o of rows) {
    const key = localDayKey(o.createdAt)
    let entry = dayStats.get(key)
    if (!entry) {
      entry = { count: 0, totalsByCurrency: new Map() }
      dayStats.set(key, entry)
    }
    entry.count += 1
    entry.totalsByCurrency.set(
      o.currency,
      (entry.totalsByCurrency.get(o.currency) ?? 0) + o.totalPrice
    )
  }
  return dayStats
}

export function daySubtotal(stats: DayStats, rates: ExchangeRates | undefined) {
  const native = Array.from(stats.totalsByCurrency.entries())
  let total = 0
  for (const [currency, amount] of native) {
    const eur = convertToEur(amount, currency, rates)
    if (eur === null) {
      return { eur: null, native }
    }
    total += eur
  }
  return { eur: total, native }
}

export function formatDayLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const startOfRow = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const dayMs = 24 * 60 * 60 * 1000
  const diffDays = Math.round((startOfToday - startOfRow) / dayMs)
  if (diffDays === 0) {
    return "Today"
  }
  if (diffDays === 1) {
    return "Yesterday"
  }
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
}
